import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";
import { useFrame } from "react-frame-component";

import { browserLocalStorage } from "@/react/lib/browserStorage";

export const useLocalStorageState = <T = unknown>(
  key: string,
  defaultValue?: T,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>] => {
  const { window } = useFrame();

  const [value, setValue] = useState(() => {
    let currentValue: T | undefined;

    try {
      if (!window) {
        throw new Error("No window object found");
      }

      const storedValue = browserLocalStorage.getItem(key);
      currentValue =
        storedValue === null ? defaultValue : JSON.parse(storedValue);
    } catch (error) {
      console.warn(
        "Failed to parse the stored localStorage value. Using the default value.",
        error,
      );
      currentValue = defaultValue;
    }

    return currentValue;
  });

  useEffect(() => {
    try {
      if (!window) {
        throw new Error("No window object found");
      }
      browserLocalStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error("Could not write to localStorage:", error);
    }
  }, [window, value, key]);

  return [value, setValue];
};
